import React, { useState, useEffect } from "react";
import SupportEmail from "./SupportEmail";

// WebView detection function
const isInWebView = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  
  // iOS WebView detection
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isIOSWebView = isIOS && !isSafari;
  
  // Android WebView detection (more precise)
  const isAndroidWebView = 
    /\bwv\b/.test(ua) || // Classic WebView marker
    (/Android/.test(ua) && !/Firefox|Chrome|SamsungBrowser/.test(ua)); // Exclude major browsers
  
  return isIOSWebView || isAndroidWebView;
};

const Login = () => {
  const [paymentId, setPaymentId] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [deleteAccountId, setDeleteAccountId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('login');
  const [isWebView, setIsWebView] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [showSafariNotification, setShowSafariNotification] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const apiUrl = 'https://api.omegavoid.com';
  
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      window.location.href = '/map';
    }
  }, []);
  
  // Check for WebView and Safari on component mount
  useEffect(() => {
    setIsWebView(isInWebView());
  }, []);
  
  const handleLogin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    
    if (!paymentId.trim()) {
      setErrorMessage('Please enter your Login ID');
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(`${apiUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_id: paymentId
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.id_token) {
        localStorage.setItem('authToken', data.id_token);
            if (data.messaging_handle) {
      localStorage.setItem('messaging_handle', data.messaging_handle);
    }
        if (data.tokens) {
          localStorage.setItem('userTokens', data.tokens.toString());
        }
        
        if (data.temp_user_id) {
          localStorage.setItem('tempUserId', data.temp_user_id);
        }
        
        if (data.isPremium === true) {
          localStorage.setItem('isPremiumUser', 'true');
        } 
        setSuccessMessage('Login successful! Redirecting...');
        
        setTimeout(() => {
          window.location.href = '/map';
        }, 1000);
      } else {
        setErrorMessage(data.message || 'Login failed. Please check your Login ID.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage('Network error. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRecovery = async (e) => {
    e.preventDefault();
    
    if (!recoveryCode.trim()) {
      setErrorMessage('Please enter your Recovery Code');
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(`${apiUrl}/recover-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recovery_code: recoveryCode
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.id_token) {
        localStorage.setItem('authToken', data.id_token);
        
        if (data.tokens) {
          localStorage.setItem('userTokens', data.tokens.toString());
        }
        
        setSuccessMessage(`Recovery successful! Your Login ID is: ${data.payment_id}. Save it securely. Redirecting...`);
        
        setTimeout(() => {
          window.location.href = '/login';
        }, 30000);
      } else {
        setErrorMessage(data.message || 'Recovery failed. Please check your Recovery Code.');
      }
    } catch (error) {
      console.error('Recovery error:', error);
      setErrorMessage('Network error. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    
    if (!deleteAccountId.trim()) {
      setErrorMessage('Please enter your Login ID');
      return;
    }

    if (!showDeleteConfirmation) {
      setShowDeleteConfirmation(true);
      setErrorMessage('');
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(`${apiUrl}/delete-account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_id: deleteAccountId
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccessMessage('Account archived successfully.');
        setDeleteAccountId('');
        setShowDeleteConfirmation(false);
        
        // Clear any stored data
        localStorage.removeItem('authToken');
        localStorage.removeItem('userTokens');
        localStorage.removeItem('tempUserId');
        localStorage.removeItem('isPremiumUser');
        
      } else {
        setErrorMessage(data.message || 'Account deletion failed. Please check your Login ID.');
        setShowDeleteConfirmation(false);
      }
    } catch (error) {
      console.error('Delete account error:', error);
      setErrorMessage('Network error. Please try again later.');
      setShowDeleteConfirmation(false);
    } finally {
      setIsLoading(false);
    }
  };

  const formatRecoveryCode = (input) => {
    const cleaned = input.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    
    let formatted = '';
    for (let i = 0; i < cleaned.length && i < 16; i++) {
      if (i > 0 && i % 4 === 0 && formatted.length < 19) {
        formatted += '-';
      }
      formatted += cleaned[i];
    }
    
    return formatted;
  };

  const handleRecoveryCodeChange = (e) => {
    const formatted = formatRecoveryCode(e.target.value);
    setRecoveryCode(formatted);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setErrorMessage('');
    setSuccessMessage('');
    setShowDeleteConfirmation(false);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h1>Login to Omega Void&reg;</h1>
        <p className="login-intro">
          Access the secure, anonymous messaging platform using your Login ID
        </p>
        
        <div className="login-tabs">
          <button 
            className={`tab-button ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => handleTabChange('login')}
          >
            Login with Login ID
          </button>
          <button 
            className={`tab-button ${activeTab === 'recovery' ? 'active' : ''}`}
            onClick={() => handleTabChange('recovery')}
          >
            Use Recovery Code
          </button>
          <button 
            className={`tab-button ${activeTab === 'delete' ? 'active' : ''}`}
            onClick={() => handleTabChange('delete')}
          >
            Delete Account
          </button>
        </div>
        
        {errorMessage && (
          <div className="error-message">
            {errorMessage}
          </div>
        )}
        
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
        
        {activeTab === 'login' && (
          <div>
            <div className="form-group">
              <label htmlFor="payment-id">Login ID:</label>
              <input 
                type="text" 
                id="payment-id" 
                placeholder="Enter the Login ID received after registration"
                value={paymentId}
                onChange={(e) => setPaymentId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin(e)}
              />
            </div>
            
            <button 
              onClick={handleLogin}
              className="login-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </div>
        )}

        {activeTab === 'recovery' && (
          <div>
            <div className="form-group">
              <label htmlFor="recovery-code">Recovery Code:</label>
              <input 
                type="text" 
                id="recovery-code" 
                placeholder="Format: XXXX-XXXX-XXXX-XXXX"
                value={recoveryCode}
                onChange={handleRecoveryCodeChange}
                onKeyPress={(e) => e.key === 'Enter' && handleRecovery(e)}
                maxLength={19}
              />
              <small className="form-hint" style={{ display: 'block', marginTop: '0.5rem', color: '#888' }}>
                Enter the Recovery Code you received during registration
              </small>
            </div>
            
            <button 
              onClick={handleRecovery}
              className="login-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Recover Access'}
            </button>
          </div>
        )}

        {activeTab === 'delete' && (
          <div>
            <div className="form-group">
              <label htmlFor="delete-account-id">Login ID:</label>
              <input 
                type="text" 
                id="delete-account-id" 
                placeholder="Enter your Login ID to delete account"
                value={deleteAccountId}
                onChange={(e) => setDeleteAccountId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleDeleteAccount(e)}
              />
              <small className="form-hint" style={{ display: 'block', marginTop: '0.5rem', color: '#888' }}>
                ⚠️ You will not able to use your tokens after this action
              </small>
            </div>

            {showDeleteConfirmation && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                color: '#856404',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                <strong>Are you sure?</strong><br />
                This will archive your account and you will not able to login.
                Click "Delete Account" again to confirm.
              </div>
            )}
            
            <button 
              onClick={handleDeleteAccount}
              className="delete-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Deleting...' : showDeleteConfirmation ? 'Confirm Delete Account' : 'Delete Account'}
            </button>
          </div>
        )}
        
        {/* Conditional rendering based on WebView detection */}
        {/* {isWebView ? ( */}
           <div className="purchase-tokens">
            <h3>Don't have a Login ID?</h3>
            <p>
              Register to receive a unique Login ID for secure access.
            </p>
            <a href="/mobile" className="purchase-btn">Register</a>
          </div>
        {/*  ) : (
           <div className="purchase-tokens">
             <h3>Don't have a Login ID?</h3>
             <p>
               Purchase tokens to receive a unique Login ID for secure access.
             </p>
             <a href="/purchase" className="purchase-btn">Purchase Tokens</a>
           </div>
         )} */}
        
        {/* <div className="login-info">
         <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <circle cx="12" cy="16" r="1"></circle>
              <path d="m12 13 0 4"></path>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
             <strong>
            About Our Anonymous Login
            </strong>
          </h3>
          <p>
            Omega Void&trade; is built for uncompromising privacy. We use a secure, token-based login system that ensures:
          </p>
          <ul>
            <li><strong>✓ One-time messages</strong></li>
            <li><strong>✓ No chats</strong></li>
            <li><strong>✓ No history</strong></li>
            <li><strong>✓ No threads</strong></li>
          </ul>
           <p>
            <br></br>
            <strong>Free by Design. Anonymous by Architecture. </strong><br></br>
Omega Void™ currently operates with <strong>no payments and no billing systems.</strong>
<br></br>There is no financial data to collect, store, or link to any Login ID, handle, or message.
Anonymity is enforced at the system level — not dependent on payment processors.
          </p>         
          

          <p>
            <strong>No usernames. No passwords.</strong> No personal data. Your <strong>Login ID</strong> is your only login credential.
          </p>
          <p>
            <strong>Backup Access:</strong> Keep your Recovery ID/Key safe - it's the only way to regain access.
          </p>
          <p>
            <strong>Zero Records:</strong> We do not associate or store your identity. All messages self-destruct on expiration.
          </p>
          <p>
            <strong>End-to-End Privacy:</strong> Our staff cannot read, recover, or view any messages. Period.
          </p>

          <p style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
            <strong>⚠️ Important Notice:</strong> While you are fully anonymous on Omega Void&trade;, we do not tolerate unlawful use of our platform.
          </p>
        </div> */}
        
        <div className="youtube-link">
          <a href="https://www.youtube.com/channel/UCTk-XVJUoqISD6jOUvALO5w" target="_blank" rel="noopener noreferrer">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#FF0000">
              <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
            </svg>
            Follow Our YouTube Channel
          </a>
        </div>

        <div className="app-store-badges" style={{
          textAlign: 'center',
          marginTop: '2rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <img
            src="/app.jpg"
            alt="Coming soon to the Apple App Store"
            style={{
              width: '150px',
              height: 'auto',
              maxWidth: '45%',
              display: 'block',
              borderRadius: '8px'
            }}
          />
          <img
            src="/play.jpg"
            alt="Coming soon to Google Play"
            style={{
              width: '150px',
              height: 'auto',
              maxWidth: '45%',
              display: 'block',
              borderRadius: '8px'
            }}
          />
        </div>
    
      </div>
      <SupportEmail />
    </div>
  );
};

export default Login;